import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  Loader2, Search, ChevronRight, Building2, User, Filter, Banknote, CheckCircle
} from "lucide-react";
import { API } from "../data/constants";

const STAGE_META = {
  terms:   { label: "Terms",   color: "bg-violet-500",  bg: "bg-violet-50",  border: "border-violet-200",  text: "text-violet-700" },
  funding: { label: "Funding", color: "bg-teal-500",    bg: "bg-teal-50",    border: "border-teal-200",    text: "text-teal-700" },
  funded:  { label: "Funded",  color: "bg-emerald-500", bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700" },
};
const LOAN_LABELS = { fix_flip: "Fix & Flip", bridge: "Bridge", dscr: "DSCR", cash_out: "Cash-Out Refi", construction: "Construction", ground_up: "Ground Up" };
import { fmtUSD } from "../utils/currency";
function fmt$(v) { return fmtUSD(v); }

export default function FundingQueue({ onDealSelect }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchQueue = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/funding/queue`, { headers })
      .then(r => setData(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-8 w-8 animate-spin text-teal-600" /></div>;

  const columns = data?.columns || {};
  const counts = data?.counts || {};
  const total = data?.total || 0;
  const allDeals = Object.values(columns).flat();
  const filtered = allDeals.filter(d => {
    const ms = !search || [d.deal_number, d.borrower_name, d.property_address, d.loan_type].filter(Boolean).some(f => f.toLowerCase().includes(search.toLowerCase()));
    const mst = stageFilter === "all" || d.stage === stageFilter;
    return ms && mst;
  });

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="funding-queue">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#0A0A0A] tracking-tight">Funding Pipeline</h1>
        <p className="text-sm text-[#666] mt-1">{total} deal{total !== 1 ? "s" : ""} in funding pipeline</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
        {Object.entries(STAGE_META).map(([key, meta]) => (
          <motion.button key={key} onClick={() => setStageFilter(stageFilter === key ? "all" : key)}
            whileHover={{ y: -2 }} whileTap={{ scale: 0.98 }} data-testid={`funding-stage-card-${key}`}
            className={`rounded-xl p-4 text-left border transition-all ${stageFilter === key ? `${meta.bg} ${meta.border} ring-2 ring-offset-1 ring-teal-300` : "bg-white border-[#eee] hover:border-[#ddd]"}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-2.5 h-2.5 rounded-full ${meta.color}`} />
              <span className="text-xs font-medium text-[#888] uppercase tracking-wider">{meta.label}</span>
            </div>
            <div className="text-2xl font-bold text-[#0A0A0A]">{counts[key] || 0}</div>
          </motion.button>
        ))}
      </div>
      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#aaa]" />
          <input type="text" placeholder="Search deals..." value={search} onChange={e => setSearch(e.target.value)}
            data-testid="funding-search" className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-[#e5e5e5] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-200 focus:border-teal-300" />
        </div>
        {stageFilter !== "all" && <button onClick={() => setStageFilter("all")} className="text-xs text-teal-600 hover:text-teal-800 flex items-center gap-1"><Filter className="h-3 w-3" /> Clear</button>}
      </div>
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {filtered.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
              <Banknote className="h-10 w-10 text-[#ccc] mx-auto mb-3" />
              <p className="text-sm text-[#888]">{search ? "No deals match" : "No deals in funding pipeline"}</p>
            </motion.div>
          ) : filtered.map((deal, i) => {
            const meta = STAGE_META[deal.stage] || STAGE_META.terms;
            return (
              <motion.button key={deal.lead_id} layout initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }} transition={{ delay: i * 0.03 }} onClick={() => onDealSelect?.(deal.lead_id)}
                data-testid={`funding-deal-${deal.lead_id}`}
                className="w-full text-left rounded-xl border border-[#eee] bg-white p-4 hover:border-teal-200 hover:shadow-sm transition-all group">
                <div className="flex items-center gap-4">
                  <div className={`shrink-0 rounded-lg px-3 py-1.5 text-xs font-semibold ${meta.bg} ${meta.text} ${meta.border} border`}>{meta.label}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm text-[#0A0A0A]">{deal.deal_number || "—"}</span>
                      <span className="text-xs text-[#999]">·</span>
                      <span className="text-xs text-[#666]">{LOAN_LABELS[deal.loan_type] || deal.loan_type}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-[#888]">
                      <span className="flex items-center gap-1"><User className="h-3 w-3" />{deal.borrower_name}</span>
                      {deal.property_address && <span className="flex items-center gap-1 truncate"><Building2 className="h-3 w-3" />{deal.property_address}</span>}
                    </div>
                  </div>
                  <div className="hidden sm:flex items-center gap-5 shrink-0">
                    <div className="text-right">
                      <div className="text-xs text-[#aaa]">Request</div>
                      <div className="text-sm font-semibold text-[#0A0A0A]">{fmt$(deal.requested_loan_amount)}</div>
                    </div>
                    <div className="w-24">
                      <div className="text-xs text-[#aaa] text-right mb-1">{deal.pct_funded || 0}% funded</div>
                      <div className="w-full h-1.5 rounded-full bg-[#f0f0f0]">
                        <div className={`h-1.5 rounded-full transition-all ${(deal.pct_funded || 0) >= 100 ? "bg-emerald-500" : "bg-teal-500"}`}
                          style={{ width: `${Math.min(deal.pct_funded || 0, 100)}%` }} />
                      </div>
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-[#ccc] group-hover:text-teal-500 transition-colors shrink-0" />
                </div>
              </motion.button>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
